import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetAll } from 'store/store.constants';
import { PropertySeasonalRule } from 'models/seasonality/SeasonalCancellationPolicy';
import {
  CalendarBodyRefreshRequest,
  FetchParams,
  LoadedDateRange,
} from '../../../pages/stackedCalendar/Calendar.types';
import updatePropertiesEntriesReducer from './reducers/updatePropertiesEntriesReducer';
import updateVisiblePropertiesReducer from './reducers/updateVisiblePropertiesReducer';
import { StackedCalendarState } from './types';

const initialState: StackedCalendarState = {
  // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  bodyRefreshRequest: undefined,
  isLoadingProperties: true,
  // @ts-expect-error TS2322 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  loadedDateRange: undefined,
  nextPropertiesOffset: 0,
  propertiesEntries: {},
  propertySeasonalRulesMap: {},
  visibleDates: [],
  visibleProperties: [],
  fetchParams: undefined,
};

const stackedCalendarSlice = createSlice({
  name: 'stackedCalendar',
  initialState,
  extraReducers: (builder) => builder.addCase(resetAll, () => initialState),
  reducers: {
    addVisibleProperties: (
      state: StackedCalendarState,
      {
        payload: { index, properties },
      }: PayloadAction<{ properties: string[]; index: number }>,
    ) => {
      state.visibleProperties.splice(index, 0, ...properties);
    },
    removeVisibleProperties: (
      state: StackedCalendarState,
      { payload: { properties } }: PayloadAction<{ properties: string[] }>,
    ) => {
      state.visibleProperties = state.visibleProperties.filter(
        (uid) => !properties.includes(uid),
      );
    },
    resetPropertiesEntries: (state: StackedCalendarState) => {
      state.propertiesEntries = {};
    },
    updatePropertySeasonalRules: (
      state: StackedCalendarState,
      { payload }: PayloadAction<Record<string, PropertySeasonalRule[]>>,
    ) => {
      state.propertySeasonalRulesMap = {
        ...state.propertySeasonalRulesMap,
        ...payload,
      };
    },
    resetStackedCalendarState: () => initialState,
    setBodyRefreshRequest: (
      state: StackedCalendarState,
      {
        payload: bodyRefreshRequest,
      }: PayloadAction<CalendarBodyRefreshRequest>,
    ) => {
      state.bodyRefreshRequest = bodyRefreshRequest;
    },
    setIsLoadingProperties: (
      state: StackedCalendarState,
      { payload: isLoadingProperties }: PayloadAction<boolean>,
    ) => {
      state.isLoadingProperties = isLoadingProperties;
    },
    setLoadedDateRange: (
      state: StackedCalendarState,
      { payload: loadedDateRange }: PayloadAction<LoadedDateRange>,
    ) => {
      state.loadedDateRange = loadedDateRange;
    },
    setNextPropertiesOffset: (
      state: StackedCalendarState,
      { payload: nextPropertiesOffset }: PayloadAction<number>,
    ) => {
      state.nextPropertiesOffset = nextPropertiesOffset;
    },
    setVisibleDates: (
      state: StackedCalendarState,
      { payload: visibleDates }: PayloadAction<Date[]>,
    ) => {
      state.visibleDates = visibleDates;
    },
    updatePropertiesEntries: updatePropertiesEntriesReducer,
    updateVisibleProperties: updateVisiblePropertiesReducer,
    setFetchParams: (
      state: StackedCalendarState,
      { payload: fetchParams }: PayloadAction<FetchParams>,
    ) => {
      state.fetchParams = fetchParams;
    },
  },
});

export const {
  addVisibleProperties,
  removeVisibleProperties,
  resetPropertiesEntries,
  resetStackedCalendarState,
  setBodyRefreshRequest,
  setIsLoadingProperties,
  setLoadedDateRange,
  setNextPropertiesOffset,
  setVisibleDates,
  updatePropertiesEntries,
  updatePropertySeasonalRules,
  updateVisibleProperties,
  setFetchParams,
} = stackedCalendarSlice.actions;

export default stackedCalendarSlice.reducer;
